/**
 * 計測テーブルの状態管理ロジック（純粋関数）。
 * TableContainer から抽出し、PR #503 系の不変条件を単体テスト可能にする。
 */
import clsx from "clsx"
import {
  DiffType,
  SampleComparisonCell,
  SampleComparisonColumn,
  SampleComparisonResponse,
  SampleComparisonRow,
  sampleMeasurements,
} from "../../../context/samplesContext"
import { MEASURE_COLOR_LIST } from "../../utils/helper"

/** 手動/空行用の最小 measure_index */
export const MANUAL_START_INDEX = 31

export type ComparisonCellData = {
  column_key: string
  spec: number | null
  result_real: number | null
  diff: number | string | null
  validate: string
  measure_type: "auto" | "manual" | ""
  is_current: boolean
  is_editable: boolean
}

export type MeasurementTableRow = {
  row_key: string
  measure_index: number // テーブル表示用の index
  data_measure_index: number | null // 現在サンプルに存在する実データの measure_index
  candidate_measure_index: number // 新規手動採寸で使用する measure_index の候補
  color: string
  measure_name: string
  result_real: number
  spec: number | string
  diff: number | string
  validate: string
  is_deleted: boolean
  measure_type: "auto" | "manual" | ""
  image_index: number | null
  has_current_measurement: boolean
  comparison_cells: ComparisonCellData[]
}

export type RowSource = {
  row?: SampleComparisonRow
  measureName: string
  normalizedName: string
  measureIndex: number | null
  currentSampleHasMeasurement: boolean
}

export type DiffPointLike = {
  measure_name: string
  spec: number
  diff: number
  validate: boolean
}

export const isVisibleMeasurement = (measurement: sampleMeasurements): boolean => {
  return !(measurement.measure_type === "auto" && measurement.is_deleted)
}

export const toValidateLabel = (validate: boolean | null | undefined, hasSpec: boolean): string => {
  if (!hasSpec) return ""
  if (validate === null || validate === undefined) return "-"
  return validate ? "OK" : "NG"
}

export const getTableRowBackgroundClass = (index: number, isDrawing: boolean): string => {
  return clsx(
    isDrawing && "bg-[#616161]",
    !isDrawing && (index % 2 === 1 ? "bg-[#272727]" : "bg-[#000000]")
  )
}

/**
 * 行が「計測中（描画中）」かどうか。
 * 高々1行のみ true になる前提（manualDrawingIndex は単一の number | null）。
 */
export const isMeasurementRowDrawing = (
  manualDrawingIndex: number | null,
  dataMeasureIndex: number | null,
  candidateMeasureIndex: number
): boolean => {
  if (manualDrawingIndex === null) return false
  return manualDrawingIndex === (dataMeasureIndex ?? candidateMeasureIndex)
}

/**
 * comparison 行ソースに対応する現在サンプルの measurement を特定する。
 * measure_index 一致に加え measure_name / normalizedName の一致も要求する（PR #503 行重複防止）。
 */
export const findCurrentMeasurement = (
  source: RowSource,
  sortedActiveCurrentMeasurements: sampleMeasurements[]
): sampleMeasurements | undefined => {
  const sourceNames = [source.measureName, source.normalizedName].filter((name) => name.trim() !== "")
  const matchesSourceName = (measurement: sampleMeasurements) =>
    sourceNames.length === 0 || sourceNames.includes(measurement.measure_name)

  if (source.currentSampleHasMeasurement && typeof source.measureIndex === "number") {
    const manualMeasurementByIndex = sortedActiveCurrentMeasurements.find(
      (measurement) =>
        measurement.measure_index === source.measureIndex &&
        measurement.measure_type === "manual" &&
        !measurement.is_deleted &&
        matchesSourceName(measurement)
    )
    if (manualMeasurementByIndex) return manualMeasurementByIndex

    const autoMeasurementByIndex = sortedActiveCurrentMeasurements.find(
      (measurement) =>
        measurement.measure_index === source.measureIndex &&
        measurement.measure_type === "auto" &&
        matchesSourceName(measurement)
    )
    if (autoMeasurementByIndex) return autoMeasurementByIndex
  }

  const nameMatchedMeasurements = sortedActiveCurrentMeasurements.filter(
    (measurement) =>
      measurement.measure_name === source.measureName ||
      measurement.measure_name === source.normalizedName
  )
  const manualMeasurement = nameMatchedMeasurements.find(
    (measurement) => measurement.measure_type === "manual" && !measurement.is_deleted
  )
  if (manualMeasurement) return manualMeasurement
  const autoMeasurement = nameMatchedMeasurements.find(
    (measurement) => measurement.measure_type === "auto"
  )
  if (autoMeasurement) return autoMeasurement
  return undefined
}

/**
 * 行ごとの candidate_measure_index を一意に割り当てる。
 * 同一 index の複数行割り当てを Set で防止する（PR #503 行重複防止）。
 */
export const allocateCandidateMeasureIndex = ({
  currentMeasurement,
  sourceMeasureIndex,
  currentMeasurementIndexes,
  assignedOriginalIndexes,
  used,
  nextOriginal,
}: {
  currentMeasurement: sampleMeasurements | undefined
  sourceMeasureIndex: number | null
  currentMeasurementIndexes: Set<number>
  assignedOriginalIndexes: Set<number>
  used: Set<number>
  nextOriginal: number
}): { candidateMeasureIndex: number; nextOriginal: number } => {
  let candidateMeasureIndex =
    currentMeasurement?.measure_index ?? sourceMeasureIndex ?? undefined
  let next = nextOriginal

  const conflictsWithCurrentMeasurement =
    !currentMeasurement &&
    typeof candidateMeasureIndex === "number" &&
    currentMeasurementIndexes.has(candidateMeasureIndex)

  if (
    typeof candidateMeasureIndex !== "number" ||
    assignedOriginalIndexes.has(candidateMeasureIndex) ||
    conflictsWithCurrentMeasurement
  ) {
    while (used.has(next) || assignedOriginalIndexes.has(next)) {
      next++
    }
    candidateMeasureIndex = next
    used.add(candidateMeasureIndex)
    next++
  }
  assignedOriginalIndexes.add(candidateMeasureIndex)

  return { candidateMeasureIndex, nextOriginal: next }
}

/**
 * current 列セルの result / measureType / diff / validate を決定する。
 * currentMeasurement が無い場合は差寸を適用せず null/空にリセットする（PR #503 残留差寸防止）。
 */
export const buildCurrentColumnCellValues = ({
  currentMeasurement,
  measureName,
  source,
  apiCell,
  displayDiffPoints,
}: {
  currentMeasurement: sampleMeasurements | undefined
  measureName: string
  source: RowSource
  apiCell: SampleComparisonCell | undefined
  displayDiffPoints: DiffPointLike[] | undefined
}): {
  spec: number | null
  resultReal: number | null
  diffValue: number | string | null
  measureType: "auto" | "manual" | ""
  validate: string
} => {
  let spec = apiCell?.spec ?? null
  // current 列は必ず currentMeasurement 由来。無い場合は residual を出さない
  const resultReal = currentMeasurement?.result_real ?? null
  const measureType: "auto" | "manual" | "" = currentMeasurement?.measure_type ?? ""
  let diffValue: number | string | null = null
  let validate = ""

  const sourceNames = [
    measureName,
    currentMeasurement?.measure_name,
    source.measureName,
    source.normalizedName,
  ]
  const diffPoint = displayDiffPoints?.find((point) =>
    sourceNames.some((sourceName) => point.measure_name === sourceName)
  )

  if (diffPoint) {
    spec = diffPoint.spec
    if (currentMeasurement) {
      diffValue = diffPoint.diff
      validate = toValidateLabel(
        diffPoint.validate,
        diffPoint.spec !== null && diffPoint.spec !== undefined
      )
    }
  }

  return { spec, resultReal, diffValue, measureType, validate }
}

export type ManualMeasurementCellInput = {
  has_current_measurement: boolean
  data_measure_index: number | null
  measure_name: string
  candidate_measure_index: number
}

/**
 * 手動計測開始時に使用する measure_index を解決する。
 * candidate が別名の既存 measurement に使われている場合は新規採番する（PR #503 行重複防止）。
 * 副作用（measurement 追加・画像チェック）は呼び出し側。
 */
export const resolveManualMeasureIndex = (
  cell: ManualMeasurementCellInput,
  existingMeasurements: Array<{
    measure_index: number
    measure_name: string
    is_deleted: boolean
  }>,
  tableCandidateIndexes: number[]
): number => {
  if (cell.has_current_measurement && cell.data_measure_index !== null) {
    return cell.data_measure_index
  }
  if (!cell.measure_name.trim()) {
    return cell.candidate_measure_index
  }

  const isIndexUsedByDifferentMeasurement = existingMeasurements.some(
    (measurement) =>
      !measurement.is_deleted &&
      measurement.measure_index === cell.candidate_measure_index &&
      measurement.measure_name !== cell.measure_name
  )
  if (!isIndexUsedByDifferentMeasurement) {
    return cell.candidate_measure_index
  }

  const existingIndexes = [
    ...existingMeasurements.map((measurement) => measurement.measure_index),
    ...tableCandidateIndexes,
  ]
  return Math.max(MANUAL_START_INDEX, ...existingIndexes) + 1
}

/**
 * ensureManualMeasurementForCell の判定・index 解決・manual エントリ生成（副作用なし）。
 * コンポーネント側は結果に応じて alert / markLegacyNormalized / updateRowSample / return のみ行う。
 *
 * - use_index: 既存 measurement または空名行。drawing 開始 index のみ返す（エントリ追加なし）
 * - blocked_no_image: 画像未選択。開始しない（呼び出し側で alert）
 * - create: 新規 manual エントリを追加してから drawing 開始
 */
export type PlanManualMeasurementResult =
  | { kind: "use_index"; measureIndex: number }
  | { kind: "blocked_no_image" }
  | { kind: "create"; measureIndex: number; entry: sampleMeasurements }

export const planManualMeasurementForCell = ({
  cell,
  sampleMeasurements,
  tableCandidateIndexes,
  selectedSampleImageIndex,
  resolution,
  sampleTime,
}: {
  cell: ManualMeasurementCellInput
  sampleMeasurements: sampleMeasurements[]
  tableCandidateIndexes: number[]
  selectedSampleImageIndex: number | null
  resolution: number
  sampleTime: number
}): PlanManualMeasurementResult => {
  if (cell.has_current_measurement && cell.data_measure_index !== null) {
    return { kind: "use_index", measureIndex: cell.data_measure_index }
  }
  if (!cell.measure_name.trim()) {
    return { kind: "use_index", measureIndex: cell.candidate_measure_index }
  }

  if (typeof selectedSampleImageIndex !== "number" || selectedSampleImageIndex < 1) {
    return { kind: "blocked_no_image" }
  }

  const measureIndex = resolveManualMeasureIndex(
    cell,
    sampleMeasurements,
    tableCandidateIndexes
  )
  const autoData = sampleMeasurements.find((m) => m.measure_type === "auto")
  const resolvedResolution =
    resolution ||
    autoData?.resolution ||
    sampleMeasurements.find((m) => typeof m.resolution === "number")?.resolution ||
    0
  const entry: sampleMeasurements = {
    measure_index: measureIndex,
    measure_name: cell.measure_name,
    image_index: selectedSampleImageIndex,
    resolution: resolvedResolution,
    result_pixel: 0,
    result_real: 0,
    result_point: [],
    measure_type: "manual",
    time: sampleTime || sampleMeasurements[0]?.time || 0,
    is_deleted: false,
  }
  return { kind: "create", measureIndex, entry }
}

/**
 * plan 結果から setManualDrawingIndex に渡す値を得る。
 * handleResultCellClick の manual 経路と同じ（null なら開始しない）。
 */
export const drawingIndexFromPlan = (
  plan: PlanManualMeasurementResult
): number | null => {
  if (plan.kind === "blocked_no_image") return null
  return plan.measureIndex
}

/**
 * 全行の isMeasurementRowDrawing を評価する（計測中は高々1行であるべき不変条件の検証用）。
 */
export const evaluateDrawingFlags = (
  manualDrawingIndex: number | null,
  rows: Array<{ data_measure_index: number | null; candidate_measure_index: number }>
): boolean[] => {
  return rows.map((row) =>
    isMeasurementRowDrawing(
      manualDrawingIndex,
      row.data_measure_index,
      row.candidate_measure_index
    )
  )
}

export type MapMeasurementTableDataParams = {
  hasComparison: boolean
  comparison: SampleComparisonResponse | null
  comparisonColumns: SampleComparisonColumn[]
  currentColumnKey: string | null
  displayDiff: DiffType | null
}

/**
 * テーブル表示用行データを構築する（mapTableData の純粋関数版）。
 */
export const mapMeasurementTableData = ({
  sampleMeasurements,
  hasComparison,
  comparison,
  comparisonColumns,
  currentColumnKey,
  displayDiff,
}: {
  sampleMeasurements: sampleMeasurements[]
  hasComparison: boolean
  comparison: SampleComparisonResponse | null
  comparisonColumns: SampleComparisonColumn[]
  currentColumnKey: string | null
  displayDiff: DiffType | null
}): MeasurementTableRow[] => {
  const activeCurrentMeasurements = sampleMeasurements.filter(isVisibleMeasurement)

  if (hasComparison && comparison) {
    const rowSources: RowSource[] = []
    const rowKeys = new Set<string>()
    const rowNameKeys = new Set<string>()
    const normalizedNameByMeasureName = new Map<string, string>()
    const createRowNameKey = (measureName: string, normalizedName: string) => {
      return normalizedName || measureName
    }
    const createRowKey = (
      measureName: string,
      normalizedName: string,
      measureIndex: number | null
    ) => {
      if (normalizedName || measureName) {
        return `name:${normalizedName || measureName}`
      }
      if (typeof measureIndex === "number") {
        return `index:${measureIndex}`
      }
      return "empty"
    }

    const addRowSource = (source: RowSource) => {
      const key = createRowKey(source.measureName, source.normalizedName, source.measureIndex)
      const nameKey = createRowNameKey(source.measureName, source.normalizedName)
      if (!source.row && nameKey && rowNameKeys.has(nameKey)) return
      if (rowKeys.has(key)) return
      rowKeys.add(key)
      if (nameKey) {
        rowNameKeys.add(nameKey)
      }
      rowSources.push(source)
    }

    const hasCurrentMeasurementForComparisonRow = (row: SampleComparisonRow) => {
      return activeCurrentMeasurements.some(
        (measurement) =>
          measurement.measure_name === row.measure_name ||
          measurement.measure_name === row.normalized_measure_name
      )
    }

    const hasReferenceCellValue = (row: SampleComparisonRow) => {
      return row.cells.some(
        (cell) =>
          cell.sample_id !== comparison.current_sample_id &&
          (cell.spec !== null ||
            cell.result_real !== null ||
            cell.diff !== null ||
            cell.validate !== null)
      )
    }

    comparison.rows.forEach((row) => {
      if (row.measure_name && row.normalized_measure_name) {
        normalizedNameByMeasureName.set(row.measure_name, row.normalized_measure_name)
      }

      if (
        row.current_sample_has_measurement &&
        !hasCurrentMeasurementForComparisonRow(row) &&
        !hasReferenceCellValue(row)
      ) {
        return
      }

      addRowSource({
        row,
        measureName: row.measure_name,
        normalizedName: row.normalized_measure_name,
        measureIndex: row.measure_index,
        currentSampleHasMeasurement: row.current_sample_has_measurement,
      })
    })

    const sortedActiveCurrentMeasurements = activeCurrentMeasurements
      .slice()
      .sort((a, b) => a.measure_index - b.measure_index)

    sortedActiveCurrentMeasurements.forEach((measurement) => {
      const normalizedName =
        normalizedNameByMeasureName.get(measurement.measure_name) ?? measurement.measure_name
      addRowSource({
        measureName: measurement.measure_name,
        normalizedName,
        measureIndex: measurement.measure_index,
        currentSampleHasMeasurement: true,
      })
    })

    rowSources.sort((a, b) => {
      const aIndex = a.measureIndex ?? Number.MAX_SAFE_INTEGER
      const bIndex = b.measureIndex ?? Number.MAX_SAFE_INTEGER
      if (aIndex !== bIndex) return aIndex - bIndex
      return a.measureName.localeCompare(b.measureName, "ja")
    })

    const used = new Set<number>([
      ...rowSources
        .map((source) => source.measureIndex)
        .filter((value): value is number => typeof value === "number"),
      ...sortedActiveCurrentMeasurements.map((measurement) => measurement.measure_index),
    ])
    const maxUsed = used.size ? Math.max(...used) : 0
    let nextOriginal = Math.max(maxUsed + 1, MANUAL_START_INDEX)
    const assignedOriginalIndexes = new Set<number>()
    const currentMeasurementIndexes = new Set(
      sortedActiveCurrentMeasurements.map((measurement) => measurement.measure_index)
    )

    const displayDiffPoints = displayDiff?.data?.points

    return MEASURE_COLOR_LIST.map((base, index) => {
      const source = rowSources[index]
      if (source) {
        const currentMeasurement = findCurrentMeasurement(source, sortedActiveCurrentMeasurements)
        const allocated = allocateCandidateMeasureIndex({
          currentMeasurement,
          sourceMeasureIndex: source.measureIndex,
          currentMeasurementIndexes,
          assignedOriginalIndexes,
          used,
          nextOriginal,
        })
        nextOriginal = allocated.nextOriginal
        const candidateMeasureIndex = allocated.candidateMeasureIndex

        const measureName = currentMeasurement?.measure_name || source.measureName
        const comparisonCells = comparisonColumns.map((column): ComparisonCellData => {
          const apiCell = source.row?.cells.find(
            (cell) => cell.column_key === column.key
          ) as SampleComparisonCell | undefined
          let spec = apiCell?.spec ?? null
          let resultReal = apiCell?.result_real ?? null
          let diffValue: number | string | null = apiCell?.diff ?? null
          let measureType: "auto" | "manual" | "" = apiCell?.measure_type ?? ""
          let validate = toValidateLabel(apiCell?.validate, spec !== null && spec !== undefined)

          if (column.key === currentColumnKey) {
            const currentValues = buildCurrentColumnCellValues({
              currentMeasurement,
              measureName,
              source,
              apiCell,
              displayDiffPoints,
            })
            spec = currentValues.spec
            resultReal = currentValues.resultReal
            measureType = currentValues.measureType
            diffValue = currentValues.diffValue
            validate = currentValues.validate
          }

          return {
            column_key: column.key,
            spec,
            result_real: resultReal,
            diff: diffValue,
            validate,
            measure_type: measureType,
            is_current: column.key === currentColumnKey,
            is_editable: column.key === currentColumnKey && column.is_editable,
          }
        })

        const currentCell = comparisonCells.find((cell) => cell.is_current)
        return {
          row_key: `comparison-${source.measureIndex ?? "none"}-${source.normalizedName || source.measureName}-${index}`,
          measure_index: index + 1,
          data_measure_index: currentMeasurement?.measure_index ?? null,
          candidate_measure_index: candidateMeasureIndex,
          color: base.color,
          measure_name: measureName,
          result_real: currentCell?.result_real ?? 0,
          spec: currentCell?.spec ?? "-",
          diff: currentCell?.diff ?? "-",
          validate: currentCell?.validate ?? "",
          is_deleted: currentMeasurement?.is_deleted ?? false,
          measure_type: currentMeasurement?.measure_type ?? currentCell?.measure_type ?? "",
          image_index: currentMeasurement?.image_index ?? null,
          // has_current_measurement は Boolean(currentMeasurement) のみ（PR #503）
          has_current_measurement: Boolean(currentMeasurement),
          comparison_cells: comparisonCells,
        }
      }

      while (used.has(nextOriginal)) {
        nextOriginal++
      }
      const assigned = nextOriginal
      used.add(assigned)
      nextOriginal++

      return {
        row_key: `comparison-empty-${assigned}`,
        measure_index: base.measure_index,
        data_measure_index: null,
        candidate_measure_index: assigned,
        color: base.color,
        measure_name: "",
        result_real: 0,
        spec: "",
        diff: "",
        validate: "",
        is_deleted: true,
        measure_type: "",
        image_index: null,
        has_current_measurement: false,
        comparison_cells: comparisonColumns.map((column) => ({
          column_key: column.key,
          spec: null,
          result_real: null,
          diff: null,
          validate: "",
          measure_type: "",
          is_current: column.key === currentColumnKey,
          is_editable: column.key === currentColumnKey && column.is_editable,
        })),
      }
    })
  }

  const active = activeCurrentMeasurements
    .sort((a, b) => a.measure_index - b.measure_index)
    .map((sm, idx) => ({
      ...sm,
      data_measure_index: sm.measure_index,
      candidate_measure_index: sm.measure_index,
      measure_index: idx + 1,
    }))

  const used = new Set<number>(active.map((sm) => sm.data_measure_index))

  const maxUsed = used.size ? Math.max(...used) : 0
  let nextOriginal = Math.max(maxUsed + 1, MANUAL_START_INDEX)

  return MEASURE_COLOR_LIST.map((base, index) => {
    const sm = active[index]
    if (sm) {
      const pt = displayDiff?.data?.points.find((p) => p.measure_name === sm.measure_name)
      return {
        row_key: `current-${sm.data_measure_index}-${index}`,
        measure_index: sm.measure_index,
        data_measure_index: sm.data_measure_index,
        candidate_measure_index: sm.candidate_measure_index,
        color: base.color,
        measure_name: sm.measure_name,
        result_real: sm.result_real,
        spec: pt?.spec ?? "-",
        diff: pt?.diff ?? "-",
        validate: pt
          ? pt.spec == null
            ? "-"
            : pt.validate == null
              ? "-"
              : pt.validate
                ? "OK"
                : "NG"
          : "",
        is_deleted: sm.is_deleted,
        measure_type: sm.measure_type,
        image_index: sm.image_index,
        has_current_measurement: true,
        comparison_cells: [],
      }
    }

    while (used.has(nextOriginal)) {
      nextOriginal++
    }
    const assigned = nextOriginal
    used.add(assigned)
    nextOriginal++

    return {
      row_key: `empty-${assigned}`,
      measure_index: base.measure_index,
      data_measure_index: null,
      candidate_measure_index: assigned,
      color: base.color,
      measure_name: "",
      result_real: 0,
      spec: "",
      diff: "",
      validate: "",
      is_deleted: true,
      measure_type: "",
      image_index: null,
      has_current_measurement: false,
      comparison_cells: [],
    }
  })
}

/**
 * handleResultCellClick（manual 経路）の決定論的遷移:
 * 1. planManualMeasurementForCell で index 解決
 * 2. create 時は updateRowSample 相当で sampleMeasurements を実更新
 * 3. 本物の mapMeasurementTableData で更新後行リストを再構築
 * 4. drawingIndexFromPlan → 全行 isMeasurementRowDrawing を評価
 *
 * 行の data/candidate を手書きで書き換えない（map の割当ロジック退行を検出するため）。
 */
export const runManualDrawingStartTransition = ({
  clickedCell,
  sampleMeasurements,
  tableCandidateIndexes,
  selectedSampleImageIndex,
  resolution,
  sampleTime,
  mapParams,
}: {
  clickedCell: ManualMeasurementCellInput
  sampleMeasurements: sampleMeasurements[]
  tableCandidateIndexes: number[]
  selectedSampleImageIndex: number | null
  resolution: number
  sampleTime: number
  mapParams: MapMeasurementTableDataParams
}): {
  plan: PlanManualMeasurementResult
  drawingIndex: number | null
  sampleMeasurementsAfter: sampleMeasurements[]
  rowsForDrawingCheck: MeasurementTableRow[]
  drawingFlags: boolean[]
} => {
  const plan = planManualMeasurementForCell({
    cell: clickedCell,
    sampleMeasurements,
    tableCandidateIndexes,
    selectedSampleImageIndex,
    resolution,
    sampleTime,
  })
  const drawingIndex = drawingIndexFromPlan(plan)

  // updateRowSample 相当（create のみエントリ追加。markLegacyNormalized は副作用のため呼び出し側）
  const sampleMeasurementsAfter =
    plan.kind === "create"
      ? [...sampleMeasurements, plan.entry]
      : sampleMeasurements

  // 本番と同じ mapMeasurementTableData で更新後テーブルを導出
  const rowsForDrawingCheck = mapMeasurementTableData({
    sampleMeasurements: sampleMeasurementsAfter,
    ...mapParams,
  })

  return {
    plan,
    drawingIndex,
    sampleMeasurementsAfter,
    rowsForDrawingCheck,
    drawingFlags: evaluateDrawingFlags(drawingIndex, rowsForDrawingCheck),
  }
}
